import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  NgZone,
  inject,
  DestroyRef,
} from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { of, Observable } from 'rxjs';
import { map, filter, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { AuthService } from '@app/core/auth/auth';
import { SessionStorageService } from '@app/core/auth/session-storage';
import { AnonymizedDataService } from '@app/features/hospital/anonymized-data/anonymized-data';
import { ReviewService } from '@app/features/hospital/reviews/reviews';
import { Review, ReviewTarget } from '@app/shared/models/review.model';
import { BreadcrumbComponent } from '@app/shared/components/breadcrumb/breadcrumb';
import { SkeletonComponent } from '@app/shared/components/skeleton/skeleton';
import { SpinnerComponent } from '@app/shared/components/spinner/spinner';
import { PaginationComponent } from '@app/shared/components/pagination/pagination';
import { EllipsisDirective } from '@app/shared/directives/ellipsis';
import { ResizableColumnDirective } from '@app/shared/directives/resizable-column';
import { FetchState } from '@app/shared/models/fetch-state.model';
import { formatId } from '@app/shared/utils/id-formats/id-formats';

type AnonymizedRecord = Record<string, unknown>;

@Component({
  selector: 'app-anonymized-data',
  standalone: true,
  imports: [
    MatTooltipModule,
    BreadcrumbComponent,
    SkeletonComponent,
    SpinnerComponent,
    PaginationComponent,
    EllipsisDirective,
    ResizableColumnDirective,
    ScrollingModule,
  ],
  templateUrl: './anonymized-data.html',
  styleUrl: './anonymized-data.scss',
})
export class AnonymizedDataComponent implements OnInit {
  reviewId = '';
  originalReviewId = '';
  dataType = '';
  dataName = '';
  recordNumber = 0;

  private readonly destroyRef = inject(DestroyRef);
  reviewState$: Observable<FetchState<Review[]>> = of({ status: 'loading' });
  reviewTargets$: Observable<ReviewTarget[]> = of([]);
  anonymizedDataState$: Observable<FetchState<AnonymizedRecord[]>> = of({
    status: 'loading',
  });
  anonymizedStatus: 'loading' | 'success' | 'error' = 'loading';

  currentPage = 1;
  totalPages = 1;
  itemsPerPageOptions = [50, 100, 200, 500];
  itemsPerPage = this.itemsPerPageOptions[0];

  dataList: AnonymizedRecord[] = [];
  tableHeaders: string[] = [];
  tooltipEnabled = false;
  isInitLoading = true;

  targetHospitalCode: string | null = null;
  targetHospitalName: string | null = null;

  @ViewChild('table') tableElementRef?: ElementRef;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly reviewService: ReviewService,
    private readonly anonymizedDataService: AnonymizedDataService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly authService: AuthService,
    private readonly sessionService: SessionStorageService,
  ) {}

  ngOnInit(): void {
    this.reviewId = this.route.snapshot.paramMap.get('id') ?? '';
    this.dataType = this.route.snapshot.paramMap.get('data-type') ?? '';

    if (this.authService.isAdminUser()) {
      this.targetHospitalCode = this.sessionService.getTargetHospitalCode();
      this.targetHospitalName = this.sessionService.getTargetHospitalName();
      if (!this.targetHospitalCode || !this.targetHospitalName) {
        this.router.navigate(['admin', 'reviews']);
      }
    }

    this.initializeReviewState();
    this.initializeTargets();
    this.initializeAnonymizedDataState();
  }

  initializeReviewState(): void {
    if (this.reviewService.isInitialLoad()) {
      this.reviewService.fetchReviews(this.targetHospitalCode);
    }
    this.reviewState$ = this.reviewService.reviewState$;
  }

  initializeTargets(): void {
    this.reviewTargets$ = this.reviewState$.pipe(
      filter(
        (state): state is { status: 'success'; data: Review[] } =>
          state.status === 'success' && !!state.data,
      ),
      map((state) => {
        const matchedReview = state.data.find(
          (review) => formatId(review.id) === this.reviewId, // urlに含まれるreviewidはxxx-xxxxの形式
        );
        if (!matchedReview || matchedReview.status !== 'processing') {
          this.router.navigate(['hospital', 'reviews']); // 直接urlを入力された場合への対策（reviewidが存在しない または レビュー中以外）
          return [];
        }
        this.originalReviewId = matchedReview.id;
        return matchedReview.targets ?? [];
      }),
    );

    this.reviewTargets$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((targets) => {
        const selectedTarget = targets.find(
          (t) => t.dataType === this.dataType,
        );

        if (!selectedTarget) {
          this.router.navigate([
            'hospital',
            'reviews',
            this.reviewId,
            'data-types',
          ]); // 直接urlを入力された場合への対策（reviewidは有効だがデータ種が存在しない）
          return;
        }

        this.dataName = selectedTarget.dataName;
        this.recordNumber = selectedTarget.recordNumber;
        this.totalPages = Math.ceil(this.recordNumber / this.itemsPerPage);
        this.fetchAnonymizedData();
      });
  }

  initializeAnonymizedDataState(): void {
    this.anonymizedDataState$ = this.anonymizedDataService.anonymizedDataState$;

    this.anonymizedDataState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        this.anonymizedStatus = state.status;

        if (state.status === 'success') {
          this.dataList = state.data ?? [];
          this.tableHeaders = this.extractDisplayKeys(this.dataList);
          this.isInitLoading = false;

          // 取得する件数に比例してレスポンスが悪くなるので、一度データを描画した後にツールチップを有効化する
          this.ngZone.onStable
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
              this.tooltipEnabled = true;
              this.cdr.detectChanges();
            });
        }
      });
  }

  fetchAnonymizedData(): void {
    this.tooltipEnabled = false; // レスポンス対策でツールチップは描画完了後に有効化する
    const offset = (this.currentPage - 1) * this.itemsPerPage;
    this.anonymizedDataService.fetchAnonymizedDatas(
      this.originalReviewId,
      this.dataType,
      this.itemsPerPage,
      offset,
    );
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.fetchAnonymizedData();
    this.scrollTableToTop();
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
    this.totalPages = Math.ceil(this.recordNumber / this.itemsPerPage);
    this.currentPage = 1;
    this.fetchAnonymizedData();
    this.scrollTableToTop();
  }

  getTooltipText(item: AnonymizedRecord, key: string): string | null {
    const value = item[key];
    return typeof value === 'string' || typeof value === 'number'
      ? String(value)
      : null;
  }

  private extractDisplayKeys(data: AnonymizedRecord[]): string[] {
    if (data.length === 0) return [];

    // resourceTypeは対象外、idを１列目に
    const keys = Object.keys(data[0]).filter((key) => key !== 'resourceType');
    const idIndex = keys.indexOf('id');

    if (idIndex > -1) {
      keys.splice(idIndex, 1);
      keys.unshift('id');
    }

    return keys;
  }

  scrollTableToTop(): void {
    if (!this.tableElementRef) return;

    this.ngZone.onStable
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const tableEl = this.tableElementRef!.nativeElement as HTMLElement;
        tableEl.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'start',
        });
      });
  }
}
